/**
 * Hook: useEventForm
 * Manages event form state, validation, and submission
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { Event } from '@/types/event.types';
import type { EventFormData, EventFormErrors } from '../types';
import {
  normalizeEventCode,
  suggestEventCode,
  validateEventCode,
  validateEventDates,
  isDraft,
  isOngoing,
} from '@/models/event';
import { getFlyerUrl, getPaymentQrUrl, isEventCodeUnique } from '@/data/eventRepository';

export type EventCodeStatus = 'idle' | 'checking' | 'ok' | 'taken';

/** ISO timestamp → local 'YYYY-MM-DDTHH:mm' for <input type="datetime-local"> */
function toDateTimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const initialFormData: EventFormData = {
  code: '',
  name: '',
  description: '',
  location: '',
  start_date: null,
  end_date: null,
  base_price: 0,
  agenda: '',
  registration_form_type: 'SIMPLE',
  allow_public_guests: false,
  signature_mode: 'ON_SITE',
  registration_deadline: '',
  registration_declaration: '',
  payment_instructions: '',
  flyer: null,
  payment_qr: null,
  remove_payment_qr: false,
};

export function useEventForm(existingEvent?: Event) {
  // Allow editing past dates if event is draft or ongoing
  const allowPastDates = existingEvent
    ? isDraft(existingEvent) || isOngoing(existingEvent)
    : false;

  // Track if we've already loaded the flyer to prevent double execution
  const flyerLoadedRef = useRef(false);

  // New events: the code follows the name until the user types their own
  const codeManuallyEdited = useRef(false);
  const [codeStatus, setCodeStatus] = useState<EventCodeStatus>('idle');

  const [formData, setFormData] = useState<EventFormData>(initialFormData);
  const [originalData, setOriginalData] =
    useState<EventFormData>(initialFormData);
  const [errors, setErrors] = useState<EventFormErrors>({});
  const [touched, setTouched] = useState<
    Partial<Record<keyof EventFormData, boolean>>
  >({});
  const [flyerPreview, setFlyerPreview] = useState<string | null>(null);
  const [paymentQrPreview, setPaymentQrPreview] = useState<string | null>(null);

  // Load existing event data
  useEffect(() => {
    if (existingEvent && !flyerLoadedRef.current) {
      const eventData: EventFormData = {
        code: existingEvent.code,
        name: existingEvent.name,
        description: existingEvent.description || '',
        location: existingEvent.location || '',
        start_date: new Date(existingEvent.start_date),
        end_date: new Date(existingEvent.end_date),
        base_price: existingEvent.base_price || 0,
        agenda: existingEvent.agenda || '',
        flyer: null,
        payment_qr: null,
        remove_payment_qr: false,
        registration_form_type:
          existingEvent.registration_form_type === 'FULL_CONVENTION' ? 'FULL_CONVENTION' : 'SIMPLE',
        allow_public_guests: existingEvent.allow_public_guests ?? false,
        signature_mode: existingEvent.signature_mode === 'DIGITAL' ? 'DIGITAL' : 'ON_SITE',
        registration_deadline: toDateTimeLocal(existingEvent.registration_deadline),
        registration_declaration: existingEvent.registration_declaration || '',
        payment_instructions: existingEvent.payment_instructions || '',
      };
      setFormData(eventData);
      setOriginalData(eventData);

      const qrUrl = getPaymentQrUrl(existingEvent.payment_qr_filename);
      if (qrUrl) setPaymentQrPreview(qrUrl);

      // Load existing flyer preview if available
      if (existingEvent.flyer_filename) {
        const flyerUrl = getFlyerUrl(existingEvent.flyer_filename);
        if (flyerUrl) {
          setFlyerPreview(flyerUrl);
          flyerLoadedRef.current = true;
        }
      } else {
        flyerLoadedRef.current = true;
      }
    }
  }, [existingEvent]);

  // Live uniqueness check (debounced). When the code was auto-suggested and is
  // taken, move to the first free variant (CNO26 → CNO26-2) instead of nagging.
  useEffect(() => {
    const code = formData.code;
    if (!code || validateEventCode(code) || (existingEvent && code === existingEvent.code)) {
      setCodeStatus('idle');
      return;
    }

    let cancelled = false;
    setCodeStatus('checking');
    const timer = setTimeout(async () => {
      if (await isEventCodeUnique(code, existingEvent?.id)) {
        if (!cancelled) setCodeStatus('ok');
        return;
      }
      if (!existingEvent && !codeManuallyEdited.current) {
        for (let n = 2; n <= 20; n++) {
          const candidate = `${code}-${n}`;
          if (candidate.length > 20) break;
          if (await isEventCodeUnique(candidate)) {
            if (!cancelled) setFormData((prev) => (prev.code === code ? { ...prev, code: candidate } : prev));
            return;
          }
        }
      }
      if (!cancelled) setCodeStatus('taken');
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.code, existingEvent]);

  // Validate single field
  const validateField = useCallback(
    (field: keyof EventFormData, value: unknown): string | undefined => {
      switch (field) {
        case 'code': {
          if (!value || (typeof value === 'string' && !value.trim())) {
            return 'El código es requerido';
          }
          const codeError = validateEventCode(value as string);
          return codeError || undefined;
        }

        case 'name':
          if (!value || (typeof value === 'string' && !value.trim())) {
            return 'El nombre es requerido';
          }
          if (typeof value === 'string' && value.trim().length < 3) {
            return 'El nombre debe tener al menos 3 caracteres';
          }
          break;

        case 'location':
          if (!value || (typeof value === 'string' && !value.trim())) {
            return 'La ubicación es requerida';
          }
          break;

        case 'start_date':
          if (!value) {
            return 'La fecha de inicio es requerida';
          }
          if (formData.end_date) {
            const dateError = validateEventDates(
              value as Date,
              formData.end_date,
              allowPastDates, // Allow past dates for draft/ongoing events
            );
            return dateError || undefined;
          }
          break;

        case 'end_date':
          if (!value) {
            return 'La fecha de finalización es requerida';
          }
          if (formData.start_date) {
            const dateError = validateEventDates(
              formData.start_date,
              value as Date,
              allowPastDates, // Allow past dates for draft/ongoing events
            );
            return dateError || undefined;
          }
          break;

        case 'base_price':
          if (value === null || value === undefined) {
            return 'El precio base es requerido';
          }
          if (typeof value === 'number' && value < 0) {
            return 'El precio no puede ser negativo';
          }
          break;
      }
      return undefined;
    },
    [formData.start_date, formData.end_date, allowPastDates],
  );

  // Validate all fields
  const validateAll = useCallback((): boolean => {
    const newErrors: EventFormErrors = {};

    (Object.keys(formData) as Array<keyof EventFormData>).forEach((field) => {
      if (
        field !== 'flyer' &&
        field !== 'payment_qr' &&
        field !== 'remove_payment_qr' &&
        field !== 'description' &&
        field !== 'agenda'
      ) {
        // Description and agenda are optional; files are validated on selection
        const error = validateField(field, formData[field]);
        if (error) {
          newErrors[field] = error;
        }
      }
    });

    if (!newErrors.code && codeStatus === 'taken') {
      newErrors.code = 'Ya existe un evento con este código';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateField, codeStatus]);

  // Update field value
  const updateField = useCallback(
    <K extends keyof EventFormData>(field: K, value: EventFormData[K]) => {
      let nextValue: EventFormData[K] = value;
      const patch: Partial<EventFormData> = {};

      // Setting the start date also sets the end date (single-day default). The end
      // date follows only while it still equals the previous start date; a later or
      // customized end date is left alone unless it would fall before the new start.
      if (field === 'start_date' && value) {
        const newStart = value as Date;
        const end = formData.end_date;
        if (!end || end.getTime() === formData.start_date?.getTime() || end.getTime() < newStart.getTime()) {
          patch.end_date = newStart;
          setErrors((prev) => ({ ...prev, end_date: undefined }));
        }
      }

      if (field === 'code') {
        nextValue = normalizeEventCode(String(value)) as EventFormData[K];
        // Clearing the field hands control back to the name-based suggestion
        codeManuallyEdited.current = String(nextValue) !== '';
      } else if (!existingEvent && !codeManuallyEdited.current && (field === 'name' || field === 'start_date')) {
        const suggestion = suggestEventCode(
          field === 'name' ? String(value) : formData.name,
          field === 'start_date' ? (value as Date | null) : formData.start_date,
        );
        if (suggestion) {
          patch.code = suggestion;
          setErrors((prev) => ({ ...prev, code: undefined }));
        }
      }

      setFormData((prev) => ({ ...prev, ...patch, [field]: nextValue }));

      // Validate after update
      if (touched[field]) {
        const error = validateField(field, nextValue);
        setErrors((prev) => ({
          ...prev,
          [field]: error,
        }));
      }

      // Re-validate dates when either date changes
      if ((field === 'start_date' || field === 'end_date') && value) {
        const endForCheck = patch.end_date ?? formData.end_date;
        if (field === 'start_date' && endForCheck) {
          const dateError = validateEventDates(
            value as Date,
            endForCheck,
            allowPastDates, // Allow past dates for draft/ongoing events
          );
          setErrors((prev) => ({
            ...prev,
            start_date: dateError || undefined,
            end_date: dateError || undefined,
          }));
        } else if (field === 'end_date' && formData.start_date) {
          const dateError = validateEventDates(
            formData.start_date,
            value as Date,
            allowPastDates, // Allow past dates for draft/ongoing events
          );
          setErrors((prev) => ({
            ...prev,
            start_date: dateError || undefined,
            end_date: dateError || undefined,
          }));
        }
      }
    },
    [formData, touched, validateField, allowPastDates, existingEvent],
  );

  const handlePaymentQrSelect = useCallback((file: File | null) => {
    setFormData((prev) => ({ ...prev, payment_qr: file, remove_payment_qr: false }));
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPaymentQrPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  }, []);

  const handlePaymentQrRemove = useCallback(() => {
    setFormData((prev) => ({ ...prev, payment_qr: null, remove_payment_qr: true }));
    setPaymentQrPreview(null);
  }, []);

  /** Set a code without marking it as user-typed (e.g. the default EVT-YYYY-NNN) */
  const applyGeneratedCode = useCallback((code: string) => {
    setFormData((prev) => ({ ...prev, code }));
  }, []);

  /** Back to the name-based suggestion after the user edited the code by hand */
  const resetCodeSuggestion = useCallback(() => {
    codeManuallyEdited.current = false;
    const suggestion = suggestEventCode(formData.name, formData.start_date);
    if (suggestion) {
      setFormData((prev) => ({ ...prev, code: suggestion }));
      setErrors((prev) => ({ ...prev, code: undefined }));
    }
  }, [formData.name, formData.start_date]);

  // Handle field blur
  const handleBlur = useCallback(
    (field: keyof EventFormData) => {
      setTouched((prev) => ({ ...prev, [field]: true }));
      const error = validateField(field, formData[field]);
      setErrors((prev) => ({
        ...prev,
        [field]: error,
      }));
    },
    [formData, validateField],
  );

  // Handle flyer file selection
  const handleFlyerSelect = useCallback((file: File | null) => {
    setFormData((prev) => ({ ...prev, flyer: file }));

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFlyerPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setFlyerPreview(null);
    }
  }, []);

  // Reset form
  const reset = useCallback(() => {
    setFormData(initialFormData);
    setOriginalData(initialFormData);
    setErrors({});
    setTouched({});
    setFlyerPreview(null);
    setPaymentQrPreview(null);
  }, []);

  // Check if form has changes
  const hasChanges = useMemo(() => {
    // Check if simple fields have changed
    const simpleFieldsChanged =
      formData.code !== originalData.code ||
      formData.name !== originalData.name ||
      formData.description !== originalData.description ||
      formData.location !== originalData.location ||
      formData.base_price !== originalData.base_price ||
      formData.agenda !== originalData.agenda ||
      formData.registration_form_type !== originalData.registration_form_type ||
      formData.allow_public_guests !== originalData.allow_public_guests ||
      formData.signature_mode !== originalData.signature_mode ||
      formData.registration_deadline !== originalData.registration_deadline ||
      formData.registration_declaration !== originalData.registration_declaration ||
      formData.payment_instructions !== originalData.payment_instructions;

    // Check if dates have changed
    const datesChanged =
      formData.start_date?.getTime() !== originalData.start_date?.getTime() ||
      formData.end_date?.getTime() !== originalData.end_date?.getTime();

    // Check if flyer has changed
    const flyerChanged = formData.flyer !== null;
    const paymentQrChanged = formData.payment_qr != null || formData.remove_payment_qr;

    return simpleFieldsChanged || datesChanged || flyerChanged || paymentQrChanged;
  }, [formData, originalData]);

  return {
    formData,
    errors,
    touched,
    flyerPreview,
    hasChanges,
    updateField,
    handleBlur,
    handleFlyerSelect,
    validateAll,
    reset,
    codeStatus,
    applyGeneratedCode,
    resetCodeSuggestion,
    paymentQrPreview,
    handlePaymentQrSelect,
    handlePaymentQrRemove,
  };
}
